import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredComics, reviews } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = featuredComics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50 pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-50 mb-4">作品不存在</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = featuredComics.filter((c) => c.id !== comic.id).slice(0, 3);
  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: `2026-${String(Math.max(1, 7 - Math.floor(i / 4))).padStart(2, '0')}-${String(Math.max(1, 15 - i)).padStart(2, '0')}`,
  }));

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full pt-24 pb-16 md:pt-32 md:pb-24 bg-background-50">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-300 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.category === '热血战斗' ? 'rexue' : comic.category === '恋爱甜宠' ? 'lianai' : comic.category === '悬疑惊悚' ? 'xuanyi' : comic.category === '奇幻冒险' ? 'qihuan' : comic.category === '搞笑日常' ? 'xiju' : 'dushi'}`} className="hover:text-foreground-300 transition-colors">
                {comic.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-200 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
              <div className="lg:w-[55%]">
                <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-6">
                  作品档案
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-foreground-50 leading-tight">
                  {comic.title}
                </h1>
                <p className="mt-6 text-base md:text-lg text-foreground-400 leading-relaxed max-w-xl">
                  {comic.description}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <span className="px-3 py-1.5 text-xs bg-background-200/60 text-foreground-400 rounded-full">
                    {comic.category}
                  </span>
                  <span className="px-3 py-1.5 text-xs bg-background-200/60 text-foreground-400 rounded-full">
                    评分 {comic.rating}
                  </span>
                  <span className="px-3 py-1.5 text-xs bg-background-200/60 text-foreground-400 rounded-full">
                    {comic.status}
                  </span>
                  <span className="px-3 py-1.5 text-xs bg-background-200/60 text-foreground-400 rounded-full">
                    共{comic.chapters}话
                  </span>
                </div>
              </div>

              <div className="lg:w-[45%]">
                <div className="rounded-3xl bg-[#1a2e2a] p-8 md:p-10">
                  <div className="flex items-center justify-between mb-8">
                    <span className="text-xs text-foreground-400/70">官方评分 {comic.rating}</span>
                    <i className="ri-more-fill text-foreground-500 text-lg cursor-pointer"></i>
                  </div>
                  <blockquote className="text-xl md:text-2xl font-heading text-foreground-50 leading-relaxed mb-10">
                    &ldquo;{comic.status === '已完结' ? '已完结，全话畅读' : '热更连载中，每周更新'}&rdquo;
                  </blockquote>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-background-300/50 flex items-center justify-center">
                        <i className="ri-user-line text-foreground-400"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-200">{comic.author}</div>
                        <div className="text-xs text-foreground-500">原著 · {comic.artist} 插画</div>
                      </div>
                    </div>
                    <button className="px-5 py-2.5 text-sm bg-foreground-50/10 hover:bg-foreground-50/20 text-foreground-200 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2">
                      开始阅读 <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-8">
              章节目录
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {chapterList.map((ch) => (
                <a
                  key={ch.num}
                  href="#"
                  className="flex items-center justify-between p-4 rounded-xl bg-background-200/40 hover:bg-background-200 transition-colors duration-200 cursor-pointer group"
                >
                  <span className="text-sm text-foreground-300 group-hover:text-foreground-200 transition-colors">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-600">{ch.date}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full py-16 md:py-20 bg-background-50">
            <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
              <h2 className="font-heading text-2xl md:text-3xl font-extralight text-foreground-50 mb-10">
                同好也在看
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.slug}`}
                    className="group relative rounded-2xl overflow-hidden bg-background-200/50 aspect-[3/4] block cursor-pointer"
                  >
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20cover%20art%2C%20dramatic%20character%20portrait%2C%20dark%20moody%20background%2C%20professional%20korean%20comic%20artwork%2C%20cinematic%20lighting%2C%20rich%20details&width=500&height=667&seq=related-comic-01&orientation=portrait"
                      alt={rc.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-transparent to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-base font-semibold text-foreground-50">{rc.title}</h3>
                      <p className="text-sm text-foreground-400 mt-1">{rc.updateInfo}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-4">
              最新书评
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-10">
              读者讨论 / 精彩点评
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[reviews.slice(0, 2), reviews.slice(2, 4), reviews.slice(0, 2)].map((col, colIdx) => (
                <div key={colIdx} className="flex flex-col gap-6">
                  {col.map((review, rIdx) => (
                    <div
                      key={`${colIdx}-${rIdx}`}
                      className="p-6 rounded-2xl bg-background-200/50"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={review.avatar}
                          alt={review.username}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground-200">{review.username}</div>
                          <div className="text-xs text-foreground-600">{review.handle}</div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-400 leading-relaxed">
                        &ldquo;{review.content}&rdquo;
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://metcw.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: comic.category,
                  item: `http://metcw.com/category/${
                    comic.category === '热血战斗' ? 'rexue' :
                    comic.category === '恋爱甜宠' ? 'lianai' :
                    comic.category === '悬疑惊悚' ? 'xuanyi' :
                    comic.category === '奇幻冒险' ? 'qihuan' :
                    comic.category === '搞笑日常' ? 'xiju' : 'dushi'
                  }`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: comic.title,
                },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: comic.title,
              description: comic.description,
              author: {
                '@type': 'Person',
                name: comic.author,
              },
              datePublished: '2025-01-01',
              inLanguage: 'zh-CN',
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}